import { useEffect, useMemo, useState } from "react";
import {
  ISurgeItemRef,
  ISurgeItemScope,
  ISurgeRule,
  ISurgeRuleBody,
  SurgeDaysMode,
  SurgeTimeMode,
} from "@/api_framework/api_modals/SurgePricing";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { useConfigurationCityListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityListAPI";
import { useSurgePricingAPI } from "@/app/hooks/api_hooks/surge_pricing/useSurgePricingAPI";
import { useClinicListAPI } from "@/app/hooks/api_hooks/org/useClinicListAPI";
import { useOrgServicesByFilterAPI } from "@/app/hooks/api_hooks/org/useOrgServicesByFilterAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";

export const ALL = "all_inclusive";
export const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const STATUS_ITEMS: ISelectMenuItemData[] = [
  { id: "ACTIVE", title: "Active" },
  { id: "INACTIVE", title: "Inactive" },
];
export const ITEM_TYPE_ITEMS: ISelectMenuItemData[] = [
  { id: "SERVICE", title: "Services" },
  { id: "BOM", title: "BOMs" },
];
export const DAYS_MODE_ITEMS: ISelectMenuItemData[] = [
  { id: "ALL", title: "Every Day" },
  { id: "WEEKDAYS", title: "Weekdays (Mon–Fri)" },
  { id: "WEEKEND", title: "Weekend (Sat–Sun)" },
  { id: "CUSTOM", title: "Pick Days" },
];
export const TIME_MODE_ITEMS: ISelectMenuItemData[] = [
  { id: "ALL_DAY", title: "All Day" },
  { id: "DAY", title: "Day (08:00–19:00)" },
  { id: "NIGHT", title: "Night (19:00–08:00 next day)" },
  { id: "CUSTOM", title: "Custom Hours" },
];
export const CLINIC_SCOPE_OPTIONS = [
  { id: "CITIES", title: "All clinics in these cities" },
  { id: "CLINICS", title: "Selected clinics" },
];
export const DAY_ITEMS: ISelectMenuItemData[] = DAY_LABELS.map((title, idx) => ({ id: String(idx), title }));

// Cities, clinics, appointment types and persona tags as select-menu items.
// Lists are memoised so dropdowns don't rebuild/reset on every keystroke.
export const useSurgeLookups = ({ withPersonas = true }: { withPersonas?: boolean } = {}) => {
  const { getRouteKey } = useRouter();
  const { routeState } = useRouteInfo(getRouteKey("CONFIGURATION", "id"));
  const { cityList } = routeState.state as IConfigurationListState;
  const fetchCities = useConfigurationCityListAPI();
  const { execute: fetchClinics, clinicList } = useClinicListAPI();
  const { execute: fetchServiceTypes, serviceTypes } = useOrgServicesByFilterAPI();
  // Surge's own cached list of common tags (the coupon persona API scans every customer).
  const { listPersonas } = useSurgePricingAPI();
  const [userpersona, setUserpersona] = useState<string[]>([]);

  useEffect(() => {
    if (!cityList?.length) fetchCities();
    fetchClinics();
    // Only types with active, bookable sub-services.
    fetchServiceTypes({ filterType: "staff_setup" });
    if (withPersonas) listPersonas().then((tags) => setUserpersona(tags ?? []));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once per page

  const rawCityItems = useSelectMenuReducer(cityList, "name", "id");
  const rawClinicItems = useSelectMenuReducer(clinicList, "clinic_name", "id");
  const rawTypeItems = useSelectMenuReducer(serviceTypes, "name", "id");
  const cityItems = useMemo(() => rawCityItems.map((c) => ({ ...c, id: String(c.id) })), [cityList]); // eslint-disable-line react-hooks/exhaustive-deps
  const clinicItems = useMemo(() => rawClinicItems, [clinicList]); // eslint-disable-line react-hooks/exhaustive-deps
  const appointmentTypeItems = useMemo(() => rawTypeItems, [serviceTypes]); // eslint-disable-line react-hooks/exhaustive-deps
  const personaItems = useMemo<ISelectMenuItemData[]>(
    () => userpersona.map((p) => ({ id: p, title: p })),
    [userpersona],
  );
  // clinic id -> city id (clinic-list rows carry city_id).
  const clinicCity = useMemo(
    () =>
      Object.fromEntries(
        ((clinicList ?? []) as { id: string; city_id?: string | number }[]).map((c) => [String(c.id), String(c.city_id ?? "")]),
      ),
    [clinicList],
  );

  return { cityItems, clinicItems, clinicCity, appointmentTypeItems, personaItems, personas: userpersona };
};

// Clinics of the given cities (all clinics when none picked); cached so the list keeps its identity.
const cityClinicCache = new WeakMap<ISelectMenuItemData[], Map<string, ISelectMenuItemData[]>>();
export const clinicsInCities = (clinics: ISelectMenuItemData[], clinicCity: Record<string, string>, cityIds: string[] = []) => {
  if (!cityIds.length) return clinics;
  const key = cityIds.join("|");
  let byKey = cityClinicCache.get(clinics);
  if (!byKey) cityClinicCache.set(clinics, (byKey = new Map()));
  if (!byKey.has(key)) byKey.set(key, clinics.filter((c) => cityIds.includes(clinicCity[String(c.id)])));
  return byKey.get(key) as ISelectMenuItemData[];
};

// Same ids -> same array, so MultiSelectMenu's defaultSelected effect doesn't re-fire each render.
const pickCache = new WeakMap<ISelectMenuItemData[], Map<string, ISelectMenuItemData[]>>();
export const pickItems = (items: ISelectMenuItemData[], ids: string[] = []) => {
  const key = ids.join("|");
  let byKey = pickCache.get(items);
  if (!byKey) pickCache.set(items, (byKey = new Map()));
  if (!byKey.has(key)) byKey.set(key, items.filter((i) => ids.includes(i.id)));
  return byKey.get(key) as ISelectMenuItemData[];
};
export const pickItem = (items: ISelectMenuItemData[], id?: string | null) => items.find((i) => i.id === id);

// --- form <-> API body ------------------------------------------------------ //
export interface ISurgeRuleForm {
  name: string;
  reason: string;
  status: string;
  service_type_ids: string[];
  applicable_city: string[];
  clinic_scope: string;
  applicable_clinics: string[];
  // Coupon-style persona block: "active" = all customers, "inactive" = selected personas.
  persona_mode: string;
  persona_included: string[];
  exclude_enabled: boolean;
  persona_excluded: string[];
  // "SERVICE" / "BOM": which item sections apply (either or both).
  item_types: string[];
  service_scope: IScopeForm;
  bom_scope: IScopeForm;
  items_exclude_enabled: boolean;
  uplift_value: string;
  valid_from: string;
  valid_till: string;
  days_mode: string;
  custom_days: string[];
  time_mode: string;
  start_h: string;
  start_m: string;
  end_h: string;
  end_m: string;
  sample_clinic_id: string;
  // Preview-only sample items (never saved).
  preview_items: ISurgeItemRef[];
}

const IST_OFFSET_MS = 330 * 60 * 1000;
export const dateToEpoch = (date: string, endOfDay = false) =>
  date ? Math.floor(Date.parse(`${date}T${endOfDay ? "23:59:59" : "00:00:00"}+05:30`) / 1000) : null;
export const epochToDate = (epoch?: number | null) =>
  epoch ? new Date(epoch * 1000 + IST_OFFSET_MS).toISOString().slice(0, 10) : "";
// "2026-10-17" + "11" + "00" -> ISO with IST offset (what the APIs expect for slot times).
export const toISTIso = (date: string, hh = "00", mm = "00") => (date ? `${date}T${hh}:${mm}:00+05:30` : "");

// One item type's dropdowns: include (category → sub category → items) and the matching excludes.
export interface IScopeForm {
  category: string[];
  sub_category: string[];
  items: string[];
  ex_category: string[];
  ex_sub_category: string[];
  ex_items: string[];
}

export const EMPTY_SCOPE: IScopeForm = {
  category: [],
  sub_category: [],
  items: [],
  ex_category: [],
  ex_sub_category: [],
  ex_items: [],
};

// Item id field per type in the rule's scopes (as in coupons).
export const SCOPE_ID_FIELD = { SERVICE: "sub_service", BOM: "bom" } as const;
const SCOPE_KEY = { SERVICE: "service", BOM: "bom" } as const;
type ScopeType = keyof typeof SCOPE_KEY;
const FORM_KEY = { SERVICE: "service_scope", BOM: "bom_scope" } as const;

const scopeFormsOf = (items?: ISurgeRuleBody["items"]) => {
  const out: Pick<ISurgeRuleForm, "item_types" | "service_scope" | "bom_scope" | "items_exclude_enabled"> = {
    item_types: [],
    service_scope: EMPTY_SCOPE,
    bom_scope: EMPTY_SCOPE,
    items_exclude_enabled: false,
  };
  (Object.keys(SCOPE_KEY) as ScopeType[]).forEach((type) => {
    const included = items?.included_details?.[SCOPE_KEY[type]] as Record<string, string[]> | null | undefined;
    if (!included) return;
    const excluded = (items?.excluded_details?.[SCOPE_KEY[type]] ?? {}) as Record<string, string[]>;
    const idField = SCOPE_ID_FIELD[type];
    out.item_types.push(type);
    out[FORM_KEY[type]] = {
      category: included.category ?? [],
      sub_category: included.sub_category ?? [],
      items: included[idField] ?? [],
      ex_category: excluded.category ?? [],
      ex_sub_category: excluded.sub_category ?? [],
      ex_items: excluded[idField] ?? [],
    };
    if ([excluded.category, excluded.sub_category, excluded[idField]].some((ids) => ids?.length)) {
      out.items_exclude_enabled = true;
    }
  });
  return out;
};

const scopeBodyOf = (form: ISurgeRuleForm): ISurgeRuleBody["items"] => {
  const included_details: ISurgeItemScope = {};
  const excluded_details: ISurgeItemScope = {};
  (Object.keys(SCOPE_KEY) as ScopeType[]).forEach((type) => {
    if (!form.item_types.includes(type)) return;
    const scope = form[FORM_KEY[type]];
    const idField = SCOPE_ID_FIELD[type];
    included_details[SCOPE_KEY[type]] = { category: scope.category, sub_category: scope.sub_category, [idField]: scope.items };
    if (form.items_exclude_enabled) {
      excluded_details[SCOPE_KEY[type]] = {
        category: scope.ex_category,
        sub_category: scope.ex_sub_category,
        [idField]: scope.ex_items,
      };
    }
  });
  return { included_details, excluded_details };
};

export const EMPTY_RULE_FORM: ISurgeRuleForm = {
  name: "",
  reason: "",
  status: "ACTIVE",
  service_type_ids: [],
  applicable_city: [],
  clinic_scope: "CITIES",
  applicable_clinics: [],
  persona_mode: "active",
  persona_included: [],
  exclude_enabled: false,
  persona_excluded: [],
  item_types: [],
  service_scope: EMPTY_SCOPE,
  bom_scope: EMPTY_SCOPE,
  items_exclude_enabled: false,
  uplift_value: "",
  valid_from: epochToDate(Date.now() / 1000),
  valid_till: "",
  days_mode: "ALL",
  custom_days: [],
  time_mode: "ALL_DAY",
  start_h: "",
  start_m: "",
  end_h: "",
  end_m: "",
  sample_clinic_id: "",
  preview_items: [],
};

export const toRuleForm = (rule: ISurgeRule): ISurgeRuleForm => {
  const [startH = "", startM = ""] = (rule.time_window.start ?? "").split(":");
  const [endH = "", endM = ""] = (rule.time_window.end ?? "").split(":");
  const allClinics = rule.applicable_clinics.includes(ALL);
  return {
    ...EMPTY_RULE_FORM,
    name: rule.name,
    reason: rule.reason ?? "",
    status: rule.status,
    service_type_ids: rule.service_type_ids,
    applicable_city: rule.applicable_city.filter((c) => c !== ALL).map(String),
    clinic_scope: allClinics ? "CITIES" : "CLINICS",
    applicable_clinics: allClinics ? [] : rule.applicable_clinics,
    persona_mode: rule.persona.included.length ? "inactive" : "active",
    persona_included: rule.persona.included,
    exclude_enabled: rule.persona.excluded.length > 0,
    persona_excluded: rule.persona.excluded,
    ...scopeFormsOf(rule.items),
    uplift_value: String(rule.uplift.value),
    valid_from: epochToDate(rule.valid_from),
    valid_till: epochToDate(rule.valid_till),
    days_mode: rule.days.mode,
    custom_days: rule.days.custom.map(String),
    time_mode: rule.time_window.mode,
    start_h: startH,
    start_m: startM,
    end_h: endH,
    end_m: endM,
  };
};

export const toRuleBody = (form: ISurgeRuleForm): ISurgeRuleBody => {
  const isCustomTime = form.time_mode === "CUSTOM";
  return {
    name: form.name.trim(),
    reason: form.reason || null,
    status: form.status as ISurgeRuleBody["status"],
    service_type_ids: form.service_type_ids,
    applicable_city: form.applicable_city.length ? form.applicable_city : [ALL],
    applicable_clinics: form.clinic_scope === "CLINICS" ? form.applicable_clinics : [ALL],
    persona: {
      included: form.persona_mode === "inactive" ? form.persona_included : [],
      excluded: form.exclude_enabled ? form.persona_excluded : [],
    },
    items: scopeBodyOf(form),
    uplift: {
      type: "PERCENT",
      value: Number(form.uplift_value),
    },
    valid_from: dateToEpoch(form.valid_from) ?? 0,
    valid_till: dateToEpoch(form.valid_till, true),
    days: { mode: form.days_mode as SurgeDaysMode, custom: form.custom_days.map(Number).sort() },
    time_window: {
      mode: form.time_mode as SurgeTimeMode,
      start: isCustomTime ? `${form.start_h}:${form.start_m}` : null,
      end: isCustomTime ? `${form.end_h}:${form.end_m}` : null,
    },
  };
};

// --- list display ------------------------------------------------------------ //
export const namesOf = (ids: string[], items: ISelectMenuItemData[]) =>
  ids.map((id) => items.find((i) => i.id === String(id))?.title ?? String(id));

export const whereNames = (rule: ISurgeRuleBody, cities: ISelectMenuItemData[], clinics: ISelectMenuItemData[]) => {
  if (!rule.applicable_clinics.includes(ALL)) return namesOf(rule.applicable_clinics, clinics);
  if (!rule.applicable_city.length || rule.applicable_city.includes(ALL)) return ["All cities"];
  return namesOf(rule.applicable_city, cities);
};

export const formatTypes = (rule: ISurgeRuleBody, types: ISelectMenuItemData[]) => namesOf(rule.service_type_ids, types).join(", ");

export const formatWhere = (rule: ISurgeRuleBody, cities: ISelectMenuItemData[], clinics: ISelectMenuItemData[]) => {
  const names = whereNames(rule, cities, clinics).join(", ");
  return rule.applicable_clinics.includes(ALL) && !rule.applicable_city.includes(ALL) && rule.applicable_city.length
    ? `${names} (all clinics)`
    : names;
};

const countLabel = (ids: string[] | undefined, singular: string, plural: string) => {
  if (!ids?.length) return `no ${plural}`;
  if (ids.includes(ALL)) return `all ${plural}`;
  return `${ids.length} ${ids.length === 1 ? singular : plural}`;
};

// e.g. "Services: 1 category, all sub categories, all items · BOMs: all categories, …"
export const formatItems = (rule: ISurgeRuleBody) => {
  const { included_details: included, excluded_details: excluded } = rule.items ?? {};
  // Rules saved before category scoping have no included_details.
  if (!included) return "Old item format, edit and re-save";
  const blocks = [
    included.service && ["Services", included.service.category, included.service.sub_category, included.service.sub_service],
    included.bom && ["BOMs", included.bom.category, included.bom.sub_category, included.bom.bom],
  ].filter(Boolean) as [string, string[], string[], string[]][];
  if (!blocks.length) return "No items";
  const text = blocks
    .map(([label, category, subCategory, items]) =>
      [
        `${label}: ${countLabel(category, "category", "categories")}`,
        countLabel(subCategory, "sub category", "sub categories"),
        countLabel(items, "item", "items"),
      ].join(", "),
    )
    .join(" · ");
  const hasExclusions = [excluded?.service, excluded?.bom].some((scope) =>
    Object.values(scope ?? {}).some((ids) => ids?.length),
  );
  return hasExclusions ? `${text} (with exclusions)` : text;
};

export const formatUplift = ({ uplift }: ISurgeRuleBody) => `+${uplift.value}%`;

export const formatDate = (epoch?: number | null) => (epoch ? new Date(epoch * 1000).toLocaleDateString("en-IN") : "");

export const formatDaysTime = ({ days, time_window }: ISurgeRuleBody) => {
  const dayText =
    days.mode === "CUSTOM" ? days.custom.map((d) => DAY_LABELS[d]).join(", ") : pickItem(DAYS_MODE_ITEMS, days.mode)?.title;
  const timeText =
    time_window.mode === "CUSTOM"
      ? `${time_window.start ?? "--"}–${time_window.end ?? "--"}`
      : pickItem(TIME_MODE_ITEMS, time_window.mode)?.title;
  return `${dayText} · ${timeText}`;
};

export const formatValidity = (rule: ISurgeRuleBody) =>
  `From ${formatDate(rule.valid_from)}${rule.valid_till ? ` till ${formatDate(rule.valid_till)}` : " · no end date"}`;

export const formatWhen = (rule: ISurgeRuleBody) => `${formatDaysTime(rule)} · ${formatValidity(rule)}`;

// One-line plain summary of a (possibly unfinished) rule form.
export const ruleSummary = (
  form: ISurgeRuleForm,
  lookups: { cityItems: ISelectMenuItemData[]; clinicItems: ISelectMenuItemData[]; appointmentTypeItems: ISelectMenuItemData[] },
) => {
  const body = toRuleBody(form);
  const uplift = form.uplift_value ? formatUplift(body) : "Uplift not set";
  const types = body.service_type_ids.length ? formatTypes(body, lookups.appointmentTypeItems) : "No appointment type";
  return [
    `${uplift} on ${formatItems(body)}`,
    types,
    formatWhere(body, lookups.cityItems, lookups.clinicItems),
    formatDaysTime(body),
    formatPersona(body),
  ].join(" · ");
};

export const formatPersona = ({ persona }: ISurgeRuleBody) => {
  if (!persona.included.length && !persona.excluded.length) return "All customers";
  const parts = [];
  if (persona.included.length) parts.push(persona.included.join(", "));
  if (persona.excluded.length) parts.push(`excl. ${persona.excluded.join(", ")}`);
  return parts.join(" · ");
};
